/**
 * Warning badge when future visits may exhaust authorization, with analytics on mount.
 * @module ifFutureVisitWillExhaustBadge
 */
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import BadgeWithIconRounded from "../../../components/badgeWithIconRounded/badgeWithIconRounded";
import ToolTipUsingPortal from "../../../components/toolTipUsingPortal/toolTipUsingPortal";
import { usePayerNameContextFromSingleOrderLayout } from "../../../context/payerNameContextFromSingleOrderLayout";
import { ScreenNamesForBigQuery } from "../../../enums/eventsData";
import { EventsName } from "../../../enums/eventsName";
import { RootState } from "../../../redux/store/store";
import WarningIcon from "../../../svg/warningIcon";
import { generateFullName } from "../../../utils/generateOrderDataForTable";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";

import { useDataAccumulatorForSingleOrder } from "../../../context/dataAccumulatorForSingleOrder";
import { apiWorkerManager } from "../../../workerManagers/workerManager";

/**
 * Displays the "Potential Visits Exhausted" badge and logs a view event once per mount context.
 *
 * @returns Badge with hover tooltip for visit-exhaustion guidance
 */
const IfFutureVisitWillExhaustBadge = () => {
  const { generalOrderDetails } = useDataAccumulatorForSingleOrder();
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const orgId = getOrgIdForFetchExternalWorklist();
  const { payerName } = usePayerNameContextFromSingleOrderLayout();
  const [showTooltip, setShowTooltip] = useState(false);
  const badgeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    apiWorkerManager.enqueueAndProcess({
      patient_id: generalOrderDetails?.order?.demographics?.patient_id ?? "",
      order_id: generalOrderDetails?.order?.order_id ?? "",
      org_id: orgId ?? "",
      event_name: EventsName.VIEW_FUTURE_VISIT_WILL_EXHAUST,
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      additional_data: {
        screen_name: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
        source: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
        patient_name: generateFullName(
          generalOrderDetails?.order?.demographics?.first_name ?? "",
          "",
          generalOrderDetails?.order?.demographics?.last_name ?? "",
        ),
        patient_mrn: generalOrderDetails?.order?.demographics?.patient_id ?? "",
        coverage_type: payerName,
        payer_name: payerName,
      },
    });
  }, [generalOrderDetails, user, orgId, payerName]);

  return (
    <div
      className="multiple-past-regimen-badge--container flex items-center justify-center gap-2"
      ref={badgeRef}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <BadgeWithIconRounded
        text={"Potential Visits Exhausted"}
        icon={<WarningIcon height="16" width="16" />}
        id="if-future-visit-will-exhaust-badge"
        bgColor={"#FFE8E8"}
        textColor={"#0f0f0f"}
        borderColor={"#CC0300"}
      />
      {showTooltip && (
        <ToolTipUsingPortal
          title="Multiple Past Regimens Detected"
          placement="bottom"
          showTooltip={showTooltip}
          parentRef={badgeRef}
          offsetPx={10}
          wrapText
          maxWidth="550px"
        >
          <div>
            Treatment contains Drugs with potential exhausted visits due to
            upcoming scheduled one, please check cautiously
          </div>
        </ToolTipUsingPortal>
      )}
    </div>
  );
};

export default IfFutureVisitWillExhaustBadge;
